'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useConsent } from '@/core/components/providers/ConsentProvider';
import { jsLiteral, trackerReadySnippet } from '@/core/utils/trackers';

function onIdle(cb: () => void) {
    if (typeof window === 'undefined') return;
    if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(cb, { timeout: 4000 });
    } else {
        setTimeout(cb, 1);
    }
}

declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
    }
}

interface SiteAnalyticsProps {
    gtmId?: string | null;
    pixelId?: string | null;
    ga4Id?: string | null;
    adsId?: string | null;
    // Label of the Ads purchase conversion action (the part after "/" in send_to).
    adsConversionLabel?: string | null;
    // Merchant Center account ID (aw_merchant_id) for Ads conversions with cart data.
    merchantCenterId?: string | null;
    liveSessionId?: string | null;
    // CSP nonce for this request, forwarded from proxy.ts via the x-nonce
    // header and read server-side in (store)/layout.tsx — required on every
    // inline <Script dangerouslySetInnerHTML> below since script-src has no
    // 'unsafe-inline'.
    nonce?: string;
}

export function SiteAnalytics({ gtmId, pixelId, ga4Id, adsId, adsConversionLabel, merchantCenterId, liveSessionId, nonce }: SiteAnalyticsProps) {
    const { consent } = useConsent();
    const pathname = usePathname();
    const isFirstMount = useRef(true);
    const liveSessionInitialized = useRef(false);
    const liveSessionModule = useRef<typeof import('@livesession/browser').default | null>(null);

    useEffect(() => {
        if (consent !== 'all' || !pixelId) return;

        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', 'PageView');
        }
    }, [pathname, consent, pixelId]);

    useEffect(() => {
        if (consent !== 'all' || !liveSessionId) return;

        if (!liveSessionInitialized.current) {
            liveSessionInitialized.current = true;
            // Load off the critical hydration path — LiveSession is a nice-to-have
            // session recorder, not something that should compete with page interactivity.
            onIdle(() => {
                import('@livesession/browser').then(({ default: ls }) => {
                    liveSessionModule.current = ls;
                    ls.init(liveSessionId);
                    ls.newPageView();
                });
            });
            return;
        }
        liveSessionModule.current?.newPageView();
    }, [pathname, consent, liveSessionId]);

    // Do not load any non-essential trackers unless full consent is given
    if (consent !== 'all') return null;

    // One Google tag (gtag.js) serves both GA4 and Ads — each is just a `config` destination.
    const gtagId = ga4Id || adsId;
    // With GTM on the page gtag.js gets its own data layer: GTM treats gtag('event', …) commands in
    // `dataLayer` as events, so every e-commerce event (also pushed there as { event, ecommerce })
    // fired the container's GA4/Meta tags twice. Consent commands still go to both layers. The tag
    // only loads after full consent, so its own layer starts granted.
    const gtagLayer = gtmId ? 'gtagDataLayer' : 'dataLayer';
    const gtagInit = gtmId
        ? `
                                window.gtagDataLayer = window.gtagDataLayer || [];
                                window.gtag = function(){
                                    gtagDataLayer.push(arguments);
                                    if (arguments[0] === 'consent') (window.dataLayer = window.dataLayer || []).push(arguments);
                                };
                                (function(){gtagDataLayer.push(arguments);})('consent', 'default', { ad_storage: 'granted', analytics_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted' });
          `
        : `
                                window.dataLayer = window.dataLayer || [];
                                function gtag(){dataLayer.push(arguments);}
          `;
    const conversionLabel = adsConversionLabel?.trim().split('/').pop();
    const gtagTargets = {
        ga4Id: ga4Id || null,
        adsId: adsId || null,
        adsConversion: adsId && conversionLabel ? `${adsId}/${conversionLabel}` : null,
        merchantId: merchantCenterId?.trim() || null,
    };

    return (
        <>
            {/* Google Tag Manager - Head */}
            {gtmId && (
                <Script
                    id="gtm-init"
                    nonce={nonce}
                    strategy="lazyOnload"
                    dangerouslySetInnerHTML={{
                        __html: `
                            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                            })(window,document,'script','dataLayer',${jsLiteral(gtmId)});
                        `,
                    }}
                />
            )}

            {/* Google tag (gtag.js) — GA4 and/or Google Ads conversion tracking */}
            {gtagId && (
                <>
                    <Script
                        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gtagId)}${gtagLayer !== 'dataLayer' ? `&l=${gtagLayer}` : ''}`}
                        nonce={nonce}
                        strategy="lazyOnload"
                    />
                    <Script
                        id="gtag-init"
                        nonce={nonce}
                        strategy="lazyOnload"
                        dangerouslySetInnerHTML={{
                            __html: `
                                ${gtagInit}
                                gtag('js', new Date());
                                ${ga4Id ? `gtag('config', ${jsLiteral(ga4Id)});` : ''}
                                ${adsId ? `gtag('config', ${jsLiteral(adsId)}, { allow_enhanced_conversions: true });` : ''}
                                ${trackerReadySnippet('gtag', gtagTargets)}
                            `,
                        }}
                    />
                </>
            )}

            {/* Facebook Pixel */}
            {pixelId && (
                <Script
                    id="fb-pixel"
                    nonce={nonce}
                    strategy="lazyOnload"
                    dangerouslySetInnerHTML={{
                        __html: `
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', ${jsLiteral(pixelId)});
                            fbq('track', 'PageView');
                            ${trackerReadySnippet('fbq')}
                        `,
                    }}
                />
            )}
        </>
    );
}
